import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../api';
import styles from '../assets/noticeToQuitCreate.module.css';
import Loader from '../components/Loader';

const NoticeToQuitEdit = () => {
  const { rentId, noticeId, id } = useParams(); // id is for property-based route
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [regeneratingLetters, setRegeneratingLetters] = useState(false);
  const [notice, setNotice] = useState(null);
  const [rent, setRent] = useState(null);
  const [guardians, setGuardians] = useState([]);
  const [property, setProperty] = useState(null);
  const [room, setRoom] = useState(null);
  const [dateNotice, setDateNotice] = useState(null);
  const [propertyDateNotice, setPropertyDateNotice] = useState(null); // DatePicker value for property route
  const [minDate, setMinDate] = useState(null);
  const [selectedGuardians, setSelectedGuardians] = useState([]);
  const [checkbox, setCheckbox] = useState([]);
  const [isPropertyRoute, setIsPropertyRoute] = useState(false);
  const [sentEmailToGuardians, setSentEmailToGuardians] = useState(0);
  const [rentNTQLetters, setRentNTQLetters] = useState([]);
  const [mergeSelection, setMergeSelection] = useState({});
  const [openingMerged, setOpeningMerged] = useState(false);

  // Get user role from localStorage or context
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isClientUser = user?.role === 'CLIENT';
  const isClientContact = user?.role === 'CLIENT';

  useEffect(() => {
    loadNoticeDetails();
  }, [rentId, noticeId, id]);

  const loadNoticeDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Determine which API endpoint to call based on route parameters
      // Property-based route: /properties/:id/notice-to-quit/edit
      // Rent-based route: /rents/:rentId/notice-to-quit/edit
      let apiEndpoint;
      if (id) {
        // Property-based route
        apiEndpoint = `/properties/${id}/notice-to-quit/edit`;
      } else if (rentId) {
        // Rent-based route
        apiEndpoint = `/rents/${rentId}/notice-to-quit/edit`;
      } else {
        setError('Invalid route: missing property ID or rent ID');
        setLoading(false);
        return;
      }
      
      const response = await api.get(apiEndpoint);

      if (response.data && response.data.success !== false) {
        const data = response.data;
        
        // Laravel structure: rent, guardians, checkbox, defaultDate, property, room
        setRent(data.rent);
        setGuardians(data.guardians || []);
        setProperty(data.property);
        setRoom(data.room);
        setCheckbox(data.checkbox || []);

        // Property-route specific data
        if (data.isPropertyRoute) {
          setIsPropertyRoute(true);
          setSentEmailToGuardians(data.sent_email_to_guardians || 0);
          const letters = data.rentNTQLetters || [];
          setRentNTQLetters(letters);
          // Initialize merge selection - all letters with files checked by default (Laravel parity)
          const initialMerge = {};
          letters.forEach((letter) => {
            if (letter.hasLetter) {
              initialMerge[letter.id] = true;
            }
          });
          setMergeSelection(initialMerge);
          // Set propertyDateNotice for the DatePicker
          if (data.defaultDate) {
            setPropertyDateNotice(new Date(data.defaultDate));
          }
        }
        
        // Debug: Log guardians count for Remove NTQ button visibility
        console.log('🔍 [NOTICE TO QUIT] Guardians count:', data.guardians?.length || 0);
        console.log('🔍 [NOTICE TO QUIT] Remove NTQ button should show:', (data.guardians?.length || 0) < 2);
        
        // Set minimum date (28 days from now) - matching backend validation
        // Backend: 27 days + end of day = date must be >= 28 days from now
        const minDateObj = new Date();
        minDateObj.setDate(minDateObj.getDate() + 28);
        minDateObj.setHours(0, 0, 0, 0); // Start of day for DatePicker minDate
        setMinDate(minDateObj);
        
        // Set default date from backend (can be null after retraction)
        // Laravel allows null date_notice in edit view
        const defaultDate = data.defaultDate || data.date_notice;
        if (defaultDate) {
          const loadedDate = new Date(defaultDate);
          // Validate loaded date - if it's less than 28 days from now, reset to minDate
          const now = new Date();
          const minValidDate = new Date();
          minValidDate.setDate(now.getDate() + 27);
          minValidDate.setHours(23, 59, 59, 999);
          
          if (loadedDate <= minValidDate) {
            // Date is invalid, set to minimum valid date with time 00:00
            const invalidDate = new Date(minDateObj);
            invalidDate.setHours(0, 0, 0, 0); // Set time to 00:00 (midnight)
            setDateNotice(invalidDate);
          } else {
            // Keep the loaded date but set time to 00:00 if not already set
            loadedDate.setHours(0, 0, 0, 0); // Set time to 00:00 (midnight)
            setDateNotice(loadedDate);
          }
        } else {
          // If date_notice is null (after retraction), set to minimum date (28 days from now) with time 00:00
          // This allows user to set a new date
          const newDate = new Date(minDateObj);
          newDate.setHours(0, 0, 0, 0); // Set time to 00:00 (midnight)
          setDateNotice(newDate);
        }
        
        // Pre-populate selected guardians from checkbox array
        // In edit mode, checkbox array indicates which guardians have NTQ
        const selected = data.checkbox && Array.isArray(data.checkbox) && data.checkbox.length > 0
          ? data.guardians
              .map((guardian, index) => data.checkbox[index] ? guardian.id : null)
              .filter(id => id !== null)
          : data.guardians && data.guardians.length > 0
          ? data.guardians.map(g => g.id)
          : [];
      setSelectedGuardians(selected);
      console.log('🔍 [NOTICE TO QUIT] Initialized selectedGuardians:', selected);
      console.log('🔍 [NOTICE TO QUIT] Checkbox array:', data.checkbox);
      console.log('🔍 [NOTICE TO QUIT] Guardians array length:', data.guardians?.length);
      // Debug: Log guardian statuses to identify NTQ guardians
      if (data.guardians && data.guardians.length > 0) {
        const statusDetails = data.guardians.map((g, idx) => {
          // Calculate isPlaced and isNTQ in frontend since functions don't serialize in JSON
          // Laravel: config('users.guardians.status.placed.id') = 6
          // Laravel: config('users.guardians.status.ntq.id') = 10
          const statusId = parseInt(g.status?.id) || g.status?.id;
          const isPlacedResult = statusId === 6;
          const isNTQResult = statusId === 10;
          
          return {
            index: idx + 1,
            id: g.id,
            name: g.fullName,
            statusName: g.status?.name,
            statusId: statusId,
            isPlaced: isPlacedResult,
            isNTQ: isNTQResult
          };
        });
        console.log('🔍 [NOTICE TO QUIT] Guardian statuses:', JSON.stringify(statusDetails.slice(0, 10), null, 2)); // First 10 for debugging
        // Log NTQ guardians specifically
        const ntqGuardians = statusDetails.filter(g => g.isNTQ);
        console.log('✅ [NOTICE TO QUIT] NTQ Guardians found:', ntqGuardians.length, JSON.stringify(ntqGuardians, null, 2));
        // Log Placed guardians specifically
        const placedGuardians = statusDetails.filter(g => g.isPlaced);
        console.log('⚠️ [NOTICE TO QUIT] Placed Guardians (disabled):', placedGuardians.length, JSON.stringify(placedGuardians, null, 2));
        // Log guardians that SHOULD be Placed (status_id = 6) but isPlaced() returns false
        const shouldBePlaced = statusDetails.filter(g => g.statusId === 6 && !g.isPlaced);
        if (shouldBePlaced.length > 0) {
          console.log('❌ [NOTICE TO QUIT] BUG: Guardians with status_id=6 but isPlaced()=false:', JSON.stringify(shouldBePlaced, null, 2));
        }
        // Log status ID distribution
        const statusIdCounts = {};
        statusDetails.forEach(g => {
          const sid = g.statusId;
          statusIdCounts[sid] = (statusIdCounts[sid] || 0) + 1;
        });
        console.log('📊 [NOTICE TO QUIT] Status ID distribution:', JSON.stringify(statusIdCounts, null, 2));
      }
        
        setNotice(data);
      } else {
        const errorMsg = response.data?.message || 'Failed to load notice to quit details';
        setError(errorMsg);
        toast.error(errorMsg);
        setNotice(null);
      }
    } catch (error) {
      console.error('Error loading notice details:', error);
      const errorMsg = error.response?.data?.message || 'Failed to load notice to quit details';
      setError(errorMsg);
      toast.error(errorMsg);
      setNotice(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGuardianSelection = (guardianId, index) => {
    // Update checkbox array (Laravel edit mode behavior)
    const newCheckbox = [...checkbox];
    newCheckbox[index] = !newCheckbox[index];
    setCheckbox(newCheckbox);
    
    // Update selected guardians based on checkbox state
    setSelectedGuardians((prev) => {
      if (newCheckbox[index]) {
        // Add guardian if checked
        return prev.includes(guardianId) ? prev : [...prev, guardianId];
      } else {
        // Remove guardian if unchecked
        return prev.filter(id => id !== guardianId);
      }
    });
  };

  // Check if selected date is valid (at least 28 days from now)
  const isDateValid = () => {
    if (!dateNotice || !minDate) return false;
    
    // Create a date that's 27 days from now at end of day (matching backend validation)
    const now = new Date();
    const minValidDate = new Date();
    minValidDate.setDate(now.getDate() + 27);
    minValidDate.setHours(23, 59, 59, 999);
    
    // Date must be after (>=) minValidDate (meaning >= 28 days from now)
    return dateNotice > minValidDate;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!dateNotice) {
      toast.error('Please select an NTQ date');
      return;
    }

    if (!isDateValid()) {
      toast.error('NTQ date must be at least 28 days from now');
      return;
    }

    if (selectedGuardians.length === 0) {
      toast.error('Please select at least one guardian');
      return;
    }

    try {
      setSubmitting(true);
      
      // Format date to Laravel format: Y-m-d H:i (e.g., "2025-12-19 22:00")
      const formattedDate = `${dateNotice.getFullYear()}-${String(dateNotice.getMonth() + 1).padStart(2, '0')}-${String(dateNotice.getDate()).padStart(2, '0')} ${String(dateNotice.getHours()).padStart(2, '0')}:${String(dateNotice.getMinutes()).padStart(2, '0')}`;
      
      // Determine which API endpoint to use based on route
      let apiEndpoint;
      if (id) {
        // Property-based route: PUT /properties/:propertyId/notice-to-quit
        apiEndpoint = `/properties/${id}/notice-to-quit`;
      } else if (rentId) {
        // Rent-based route: PUT /rents/:rentId/notice-to-quit
        apiEndpoint = `/rents/${rentId}/notice-to-quit`;
      } else {
        toast.error('Invalid route: missing property ID or rent ID');
        setSubmitting(false);
        return;
      }
      
      // Laravel expects 'guardians' array (not 'guardian_ids')
      const response = await api.put(apiEndpoint, {
        date_notice: formattedDate,
        guardians: selectedGuardians, // Laravel validation expects 'guardians' key
      });

      if (response.data.success) {
        toast.success('Date notice has been updated!');
        // Redirect based on route type
        if (id) {
          navigate(`/properties/${id}`);
        } else {
          navigate('/rents/notice_to_quit');
        }
      } else {
        toast.error(response.data.message || 'Failed to update notice to quit');
      }
    } catch (error) {
      console.error('Error updating notice:', error);
      toast.error(error.response?.data?.message || 'Failed to update notice to quit');
    } finally {
      setSubmitting(false);
    }
  };


  const handleRemoveNTQ = async () => {
    if (!window.confirm('Are you sure you want to remove this Notice to Quit?')) {
      return;
    }

    try {
      setSubmitting(true);
      // Laravel route: DELETE /rents/{rent}/notice-to-quit/destroy
      const response = await api.delete(`/rents/${rentId}/notice-to-quit/destroy`);

      if (response.data.success) {
        toast.success('NTQ successfully deleted');
        // Laravel redirects to guardian show page (matching Laravel behavior)
        const guardianId = response.data.guardian_id || (guardians && guardians.length > 0 ? guardians[0].id : null);
        if (guardianId) {
          navigate(`/guardians/${guardianId}`);
        } else {
          // Fallback to NTQ list if guardian ID not available
          navigate('/rents/notice_to_quit');
        }
      } else {
        toast.error(response.data.message || 'Failed to remove notice to quit');
      }
    } catch (error) {
      console.error('Error removing notice:', error);
      toast.error(error.response?.data?.message || 'Failed to remove notice to quit');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch (error) {
      return dateString;
    }
  };

  // Property-route specific: Update notice date (simple save, no guardian selection)
  const handlePropertySave = async (e) => {
    e.preventDefault();

    // Confirm if the selected date is within 28 days from now
    if (propertyDateNotice) {
      const now = new Date();
      const inTwentyEightDays = new Date(now);
      inTwentyEightDays.setDate(inTwentyEightDays.getDate() + 28);
      if (propertyDateNotice < inTwentyEightDays) {
        const confirmed = window.confirm(
          "The selected notice date is within 28 days from now. Are you sure you want to continue?"
        );
        if (!confirmed) return;
      }
    }

    try {
      setSubmitting(true);
      // Format date to Laravel format: Y-m-d H:i
      let formattedDate = null;
      if (propertyDateNotice) {
        const d = propertyDateNotice;
        formattedDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      }
      const response = await api.put(`/properties/${id}/notice-to-quit`, {
        date_notice: formattedDate,
      });

      if (response.data.success) {
        toast.success('Date notice has been updated!');
        navigate(`/properties/${id}`);
      } else {
        toast.error(response.data.message || 'Failed to update notice to quit');
      }
    } catch (error) {
      console.error('Error updating notice:', error);
      // Show validation errors from server (28-day rule, weekday/time restrictions)
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorMessages = error.response.data.errors.map(e => e.msg || e.message).join('. ');
        toast.error(errorMessages || 'Validation failed');
      } else {
        toast.error(error.response?.data?.message || 'Failed to update notice to quit');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendEmailToGuardians = async () => {
    const propertyId = id || notice?.property?.id;
    if (!propertyId) {
      toast.error('Property ID not found');
      return;
    }

    try {
      setSendingEmail(true);
      const response = await api.post(`/properties/${propertyId}/notice-to-quit/guardians/send-emails`);

      if (response.data.success) {
        toast.success('Email sent to guardians successfully');
        // Reload notice details to update sent_email_to_guardians status
        await loadNoticeDetails();
      } else {
        toast.error(response.data.message || 'Failed to send emails to guardians');
      }
    } catch (error) {
      console.error('Error sending emails:', error);
      toast.error('Failed to send emails to guardians');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleRegenerateGuardianLetters = async () => {
    const propertyId = id || notice?.property?.id;
    if (!propertyId) {
      toast.error('Property ID not found');
      return;
    }

    if (!window.confirm('Are you sure you want to regenerate guardian letters? This will overwrite existing letters.')) {
      return;
    }

    try {
      setRegeneratingLetters(true);
      const response = await api.post(`/properties/${propertyId}/notice-to-quit/guardian-letters/regenerate`);

      if (response.data.success) {
        toast.success('Guardian letters regenerated successfully');
        // Reload notice details
        await loadNoticeDetails();
      } else {
        toast.error(response.data.message || 'Failed to regenerate guardian letters');
      }
    } catch (error) {
      console.error('Error regenerating letters:', error);
      toast.error('Failed to regenerate guardian letters');
    } finally {
      setRegeneratingLetters(false);
    }
  };

  // Download guardian letter PDF
  const handleDownloadLetter = async (letter) => {
    const propertyId = id || notice?.property?.id;
    try {
      const response = await api.get(
        `/properties/${propertyId}/notice-to-quit/guardian-letters/${letter.id}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', letter.letterFilename || `ntq_letter_${letter.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading letter:', err);
      toast.error('Failed to download letter');
    }
  };

  // Toggle merge checkbox
  const handleMergeToggle = (letterId) => {
    setMergeSelection((prev) => ({
      ...prev,
      [letterId]: !prev[letterId],
    }));
  };

  // Open merged PDFs in new tab
  const handleOpenMerged = async () => {
    const propertyId = id || notice?.property?.id;
    const selectedIds = Object.keys(mergeSelection).filter((key) => mergeSelection[key]);

    if (selectedIds.length === 0) {
      toast.error('Please select at least one letter to merge');
      return;
    }

    try {
      setOpeningMerged(true);
      const response = await api.post(
        `/properties/${propertyId}/notice-to-quit/guardian-letters/open-merged`,
        { to_merge: selectedIds },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error opening merged letters:', err);
      toast.error('Failed to open merged letters');
    } finally {
      setOpeningMerged(false);
    }
  };

  if (loading) return <Loader />;

  if (error || !notice) {
    return (
      <div style={{ backgroundColor: '#f0f4f8', minHeight: '100vh', width: '100%' }}>
        <div className={styles.container}>
          <div className={styles.row}>
            <div className={styles.colMd12}>
              <div className={`${styles.alert} ${styles.alertWarning}`}>
                <h4 className={styles.alertHeading}>Error Loading Data</h4>
                <p>{error || 'Failed to load notice to quit data from database.'}</p>
                <div style={{ marginTop: '20px' }}>
                  <button
                    className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                    onClick={() => window.location.reload()}
                    style={{ marginRight: '10px' }}
                  >
                    Retry
                  </button>
                  <button
                    className={`${styles.btn} ${styles.btnStandard}`}
                    onClick={() => navigate('/rents/notice_to_quit')}
                  >
                    Back to Notice To Quit List
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // === PROPERTY-BASED ROUTE LAYOUT (matching Laravel properties/notice_to_quit/edit.blade.php) ===
  if (isPropertyRoute) {
    return (
      <div style={{ backgroundColor: '#f0f4f8', minHeight: '100vh', width: '100%' }}>
        <div className={styles.container}>
          {/* Breadcrumbs - Laravel: Dashboard / Properties / PropertyName / Manage Notice To Quit */}
          <div className={styles.breadcrumbContainer}>
            <nav aria-label="breadcrumb">
              <ol className={styles.breadcrumb}>
                <li className={styles.breadcrumbItem}>
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className={styles.breadcrumbItem}>
                  <Link to="/properties">Properties</Link>
                </li>
                <li className={styles.breadcrumbItem}>
                  <Link to={`/properties/${id}`}>{property?.name || 'Property'}</Link>
                </li>
                <li className={`${styles.breadcrumbItem} ${styles.active}`}>
                  Manage Notice To Quit
                </li>
              </ol>
            </nav>
          </div>

          <div className={styles.row}>
            <div className={styles.colMd12}>
              <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelForm}`}>
                <div className={`${styles.panelHeading} ${styles.panelTitle}`}>
                  <h3>Manage Notice to Quit</h3>
                </div>

                <div className={styles.panelBody}>
                  {/* Property Overview - matching Laravel _property_overview partial */}
                  <div className={styles.row}>
                    <div className={styles.colMd12}>
                      <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                        <h3>Property overview</h3>
                      </div>
                      <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
                        <dl style={{ margin: 0 }}>
                          <dt style={{ float: 'left', width: '200px', fontWeight: 'bold', marginBottom: '8px' }}>Property ID:</dt>
                          <dd style={{ marginLeft: '210px', marginBottom: '8px' }}>{property?.property_id}</dd>

                          <dt style={{ float: 'left', width: '200px', fontWeight: 'bold', marginBottom: '8px' }}>Property Name:</dt>
                          <dd style={{ marginLeft: '210px', marginBottom: '8px' }}>{property?.name}</dd>

                          <dt style={{ float: 'left', width: '200px', fontWeight: 'bold', marginBottom: '8px' }}>Property Status:</dt>
                          <dd style={{ marginLeft: '210px', marginBottom: '8px' }}>{property?.statusText}</dd>

                          <dt style={{ float: 'left', width: '200px', fontWeight: 'bold', marginBottom: '8px' }}>Property address:</dt>
                          <dd style={{ marginLeft: '210px', marginBottom: '8px' }}>{property?.address_1}</dd>

                          <dt style={{ float: 'left', width: '200px', fontWeight: 'bold', marginBottom: '8px' }}>Postcode:</dt>
                          <dd style={{ marginLeft: '210px', marginBottom: '8px' }}>{property?.postcode}</dd>

                          {property?.date_live && (
                            <>
                              <dt style={{ float: 'left', width: '200px', fontWeight: 'bold', marginBottom: '8px' }}>Lease dates:</dt>
                              <dd style={{ marginLeft: '210px', marginBottom: '8px' }}>
                                {formatDate(property.date_taken_on)}
                                {property.date_taken_back && ` to ${formatDate(property.date_taken_back)}`}
                              </dd>
                            </>
                          )}

                          {property?.date_notice && (
                            <>
                              <dt style={{ float: 'left', width: '200px', fontWeight: 'bold', marginBottom: '8px' }}>Notice date:</dt>
                              <dd style={{ marginLeft: '210px', marginBottom: '8px' }}>
                                {(() => {
                                  try {
                                    const d = new Date(property.date_notice);
                                    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                                  } catch { return '-'; }
                                })()}
                              </dd>
                            </>
                          )}
                        </dl>
                        <div style={{ clear: 'both' }}></div>
                      </div>
                    </div>
                  </div>

                  {/* Info text */}
                  <p>Property can be active again if you set date field as empty. You have to remember that other actions regarding set Notice To Quit won't be restored (e.g. change rent dates).</p>

                  {/* Notice date form */}
                  <form onSubmit={handlePropertySave}>
                    <fieldset>
                      <div className="form-horizontal">
                        <div className="form-group" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                          <label htmlFor="date_notice" className="control-label" style={{ width: '41.66%', textAlign: 'right', paddingRight: '15px' }}>
                            Notice date
                          </label>
                          <div style={{ width: '220px' }}>
                            <DatePicker
                              id="date_notice"
                              selected={propertyDateNotice}
                              onChange={(date) => setPropertyDateNotice(date)}
                              showTimeSelect
                              timeFormat="HH:mm"
                              timeIntervals={15}
                              dateFormat="yyyy-MM-dd HH:mm"
                              placeholderText="YYYY-MM-DD HH:mm"
                              className="form-control"
                              isClearable
                            />
                          </div>
                        </div>
                      </div>

                      <div className="form-horizontal">
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <button
                            type="submit"
                            className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                            disabled={submitting}
                            style={{ marginBottom: '20px' }}
                          >
                            <i className="glyphicon glyphicon-ok"></i>{' '}
                            {submitting ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </fieldset>
                  </form>

                  {/* Action Buttons */}
                  <div style={{ marginBottom: '20px' }}>
                    {/* Send email to guardians - only if canManageNoticeToQuit and emails not yet sent */}
                    {property?.canManageNoticeToQuit && sentEmailToGuardians === 0 && (
                      <button
                        className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                        onClick={handleSendEmailToGuardians}
                        disabled={sendingEmail}
                        style={{ marginRight: '10px' }}
                      >
                        {sendingEmail ? 'Sending...' : 'Send email to guardians'}
                      </button>
                    )}

                    {/* Regenerate guardian letters - only if canManageNoticeToQuit and not client */}
                    {property?.canManageNoticeToQuit && !isClientContact && (
                      <button
                        className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                        onClick={handleRegenerateGuardianLetters}
                        disabled={regeneratingLetters}
                      >
                        {regeneratingLetters ? 'Regenerating...' : 'Regenerate guardian letters'}
                      </button>
                    )}
                  </div>

                  {/* Guardian Letters Section */}
                  <div className={styles.row}>
                    <div className={styles.colMd12}>
                      <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                        <h3>Guardian Letters</h3>
                      </div>
                      <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
                        {rentNTQLetters.length > 0 ? (
                          <>
                            <table className="table table-condensed" style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr>
                                  <th style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>Guardian</th>
                                  <th style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>UGD</th>
                                  <th style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>Actions</th>
                                  <th style={{ padding: '8px', borderBottom: '1px solid #ddd', textAlign: 'center' }}>Merge</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rentNTQLetters.filter((l) => l.hasLetter).map((letter) => (
                                  <tr key={letter.id}>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                      <Link to={`/guardians/${letter.guardian?.id}`}>
                                        {letter.guardian?.fullName}
                                      </Link>
                                    </td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                      {letter.guardian?.unique_guardian_number}
                                    </td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-info"
                                        style={{ backgroundColor: '#17a2b8', color: '#fff', border: 'none', padding: '3px 10px', borderRadius: '3px', cursor: 'pointer' }}
                                        onClick={() => handleDownloadLetter(letter)}
                                      >
                                        Download
                                      </button>
                                    </td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                                      <input
                                        type="checkbox"
                                        checked={!!mergeSelection[letter.id]}
                                        onChange={() => handleMergeToggle(letter.id)}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                              <button
                                type="button"
                                className="btn btn-head btn-standard"
                                style={{ backgroundColor: '#2c3e50', color: '#fff', border: 'none', padding: '6px 16px', borderRadius: '3px', cursor: 'pointer' }}
                                onClick={handleOpenMerged}
                                disabled={openingMerged || Object.values(mergeSelection).filter(Boolean).length === 0}
                              >
                                {openingMerged ? 'Opening...' : 'Open merged'}
                              </button>
                            </div>
                          </>
                        ) : (
                          <p>No guardian lived there. There is no guardian letter.</p>
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // === RENT-BASED ROUTE LAYOUT (existing guardian-centric layout) ===
  return (
    <div style={{ backgroundColor: '#f0f4f8', minHeight: '100vh', width: '100%' }}>
      <div className={styles.container}>
        {/* Breadcrumbs - Laravel structure: Dashboard / Guardians / Notice To Quit */}
        <div className={styles.breadcrumbContainer}>
          <nav aria-label="breadcrumb">
            <ol className={styles.breadcrumb}>
              <li className={styles.breadcrumbItem}>
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className={styles.breadcrumbItem}>
                <Link to="/guardians">Guardians</Link>
              </li>
              <li className={`${styles.breadcrumbItem} ${styles.active}`}>
                Notice To Quit
              </li>
            </ol>
          </nav>
        </div>

        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelForm}`}>
              <div className={`${styles.panelHeading} ${styles.panelTitle}`}>
                <h3>Manage Notice to Quit</h3>
              </div>

              <div className={styles.panelBody}>
                <form onSubmit={handleSubmit}>
                  {/* Guardian Overview - matching Laravel _guardian_overview_edit exactly */}
                  <div className={styles.row}>
                    {guardians.map((guardian, index) => {
                      // Laravel: col-md-{{ 12/$loop->count }} - dynamic column width
                      const colWidth = guardians.length === 1 ? 12 : 6;
                      const colClass = colWidth === 12 ? styles.colMd12 : styles.colMd6;

                      return (
                        <div key={guardian.id} className={colClass}>
                          <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                            <h3>Guardian {guardians.length > 1 ? `#${index + 1}` : ''}</h3>
                          </div>
                          <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
                            {/* Checkbox section - Laravel: @if($loop->count < 2) hide @endif */}
                            {guardians.length > 1 && (
                              <div className={`${styles.textCenter} ${styles.marginDown}`}>
                                {parseInt(guardian.status?.id) === 6 ? (
                                  <>
                                    <div className={styles.formGroup}>
                                      <label className={styles.checkboxInline}>
                                        <input
                                          type="checkbox"
                                          name={`guardians[guardian_${index + 1}]`}
                                          value={guardian.id}
                                          checked={checkbox[index] || false}
                                          disabled={true}
                                          className={styles.widthAuto}
                                        />
                                        Select Guardian
                                      </label>
                                    </div>
                                    <p className={`${styles.alert} ${styles.alertWarning} ${styles.strong}`}>
                                      This guardian does not have NTQ
                                    </p>
                                  </>
                                ) : parseInt(guardian.status?.id) === 10 ? (
                                  <>
                                    <div className={styles.formGroup}>
                                      <label className={styles.checkboxInline}>
                                        <input
                                          type="checkbox"
                                          name={`guardians[guardian_${index + 1}]`}
                                          value={guardian.id}
                                          checked={checkbox[index] || false}
                                          onChange={() => handleGuardianSelection(guardian.id, index)}
                                          className={styles.widthAuto}
                                        />
                                        Select Guardian
                                      </label>
                                    </div>
                                  </>
                                ) : null}
                              </div>
                            )}

                            {/* Guardian Info */}
                            <div>
                              <p><strong>Guardian ID:</strong> {guardian.unique_guardian_number || '-'}</p>
                              <p><strong>Name:</strong> {guardian.fullName || '-'}</p>
                              <p><strong>Phone Number:</strong> {guardian.user?.phone_number || '-'}</p>
                              <p></p>
                              <p><strong>Mobile Number:</strong> {guardian.user?.mobile_number || '-'}</p>
                              <p><strong>Email:</strong> {guardian.user?.email ? (
                                <a href={`mailto:${guardian.user.email}`} className={styles.mailtoInDd}>
                                  {guardian.user.email}
                                </a>
                              ) : '-'}</p>
                              <p><strong>Hear about GG:</strong> {guardian.hear_about || '-'}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Property Overview and Calendar */}
                  <div className={styles.row}>
                    {/* Property Overview */}
                    <div className={styles.colMd6}>
                      <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                        <h3>Property Overview</h3>
                      </div>
                      <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
                        <div className={styles.row}>
                          <div className={styles.colMd6}>
                            <p><strong>Property ID:</strong> {property?.property_id}</p>
                            <p><strong>Property Name:</strong> {property?.name}</p>
                            <p><strong>Property Status:</strong> {property?.statusText}</p>
                            <p><strong>Property address:</strong> {property?.address_1}</p>
                            <p><strong>Postcode:</strong> {property?.postcode}</p>
                          </div>
                          <div className={styles.colMd6}>
                            <p><strong>Room:</strong> {room?.name}</p>
                            <p><strong>Rate:</strong> £{room?.price}</p>
                            <p><strong>Rent Start Date:</strong> {formatDate(rent?.date_start)}</p>
                            <p><strong>Rent End Date:</strong> {formatDate(rent?.date_end) || '-'}</p>
                            <p><strong>Keys:</strong> {rent?.keys_provided || 0}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* NTQ Date Calendar */}
                    <div className={styles.colMd6}>
                      <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                        <h3>NTQ Date</h3>
                      </div>
                      <div className={`${styles.panelGroup} ${styles.marginDown}`}>
                        <div className={styles.row}>
                          <div className={styles.colMd12}>
                            <div id="js-rent-ntq" style={{ display: 'flex', justifyContent: 'center' }}>
                              <DatePicker
                                id="calendar-input"
                                selected={dateNotice}
                                onChange={(date) => setDateNotice(date)}
                                minDate={minDate}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="dd/MM/yyyy HH:mm"
                                placeholderText="Select date and time"
                                required
                                inline
                              />
                            </div>
                          </div>
                        </div>
                        <p className={styles.textCenter}>
                          NTQ date should be a minimum of 28 days from now (min. {minDate ? formatDate(minDate.toISOString()) : ''})
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className={styles.row}>
                    <div className={styles.colMd12}>
                      <fieldset>
                        <div className={styles.textRight}>
                          <button
                            type="submit"
                            className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                            disabled={submitting || selectedGuardians.length === 0 || !isDateValid()}
                          >
                            <i className={`${styles.glyphicon} ${styles.glyphiconOk}`}></i>{" "}
                            {submitting ? "Sending..." : "Send NTQ"}
                          </button>
                        </div>
                      </fieldset>
                    </div>
                  </div>
                </form>

                {/* Remove NTQ Button */}
                {rentId && !isPropertyRoute && (
                  <form onSubmit={(e) => { e.preventDefault(); handleRemoveNTQ(); }}>
                    <fieldset>
                      <div className={styles.textRight}>
                        <button
                          type="submit"
                          className={`${styles.btn} ${styles.btnDanger} ${styles.btnStandard}`}
                          disabled={submitting}
                        >
                          <i className={`${styles.glyphicon} ${styles.glyphiconRemove}`}></i>{" "}
                          {submitting ? "Removing..." : "Remove NTQ"}
                        </button>
                      </div>
                    </fieldset>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoticeToQuitEdit;